import { useForm as useOriginalForm } from 'react-final-form';

type Paths<T, Key extends string = ''> = {
  [K in keyof T & string]: T[K] extends Array<infer U>
    ?
        | `${Key}${K}[${number}].${Paths<U>}`
        | `${Key}${K}[${number}]`
        | `${Key}${K}`
    : T[K] extends object
      ? `${Key}${K}.${Paths<T[K]>}` | `${Key}${K}`
      : `${Key}${K}`;
}[keyof T & string];

type FindValueInUnion<T, K extends string> = T extends object
  ? K extends keyof T
    ? T[K]
    : ResolveArray<T, K>
  : never;

type ResolveArray<
  T,
  P extends string,
> = P extends `${infer ArrayName}[${number}]`
  ? FindValueInUnion<T, ArrayName> extends infer U
    ? [U] extends [never]
      ? never
      : number extends keyof U
        ? U[number]
        : never
    : never
  : never;

type GetValueByPath<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? GetValueByPath<T[K], Rest>
    : FindValueInUnion<T, K> extends infer U
      ? [U] extends [never]
        ? never
        : GetValueByPath<U, Rest>
      : never
  : P extends keyof T
    ? T[P]
    : FindValueInUnion<T, P>;

type NonUndefinedObject<T> = {
  [P in keyof T]-?: T[P];
};

export function useTypedForm<FormValues extends object>() {
  const form = useOriginalForm<FormValues>();

  const change = <P extends Paths<NonUndefinedObject<FormValues>>>(
    path: P,
    value: GetValueByPath<NonUndefinedObject<FormValues>, P>,
  ) => {
    form.change(
      path as unknown as keyof FormValues,
      value as unknown as FormValues[keyof FormValues],
    );
  };

  return { ...form, change };
}
